import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import {
    RecentActivityItem,
    RecentActivityResponseDto,
    EntityType,
} from "../../services/types/recentActivityTypes";

export interface RecentActivityState {
    sessions: RecentActivityItem[];
    resources: RecentActivityItem[];
    users: RecentActivityItem[];
    registrations: RecentActivityItem[];
    maxPerCategory: number;
}

const initialState: RecentActivityState = {
    sessions: [],
    resources: [],
    users: [],
    registrations: [],
    maxPerCategory: 5,
};

/**
 * Gets the array for a given entity type from state.
 */
function getArrayForType(state: RecentActivityState, entityType: EntityType): RecentActivityItem[] {
    switch (entityType) {
        case "session":
            return state.sessions;
        case "resource":
            return state.resources;
        case "user":
            return state.users;
        case "registration":
            return state.registrations;
        default:
            return [];
    }
}

/**
 * Sets the array for a given entity type in state.
 */
function setArrayForType(
    state: RecentActivityState,
    entityType: EntityType,
    items: RecentActivityItem[]
): void {
    switch (entityType) {
        case "session":
            state.sessions = items;
            break;
        case "resource":
            state.resources = items;
            break;
        case "user":
            state.users = items;
            break;
        case "registration":
            state.registrations = items;
            break;
    }
}

export const recentActivitySlice = createSlice({
    name: "recentActivity",
    initialState,
    reducers: {
        /**
         * Load recent activity from backend API (on login).
         * Replaces all category arrays with data from the API response.
         */
        loadRecentActivity: (
            state,
            action: PayloadAction<RecentActivityResponseDto>
        ) => {
            state.sessions = action.payload.sessions ?? [];
            state.resources = action.payload.resources ?? [];
            state.users = action.payload.users ?? [];
            state.registrations = action.payload.registrations ?? [];
        },

        /**
         * Add or update a recent activity item (during session, local only).
         * If the entity already exists, it's moved to the front with updated timestamp.
         * If the category is at max capacity, the oldest item is removed.
         */
        addRecentActivity: (state, action: PayloadAction<RecentActivityItem>) => {
            const item = action.payload;
            const entityType = item.entityType;
            const currentArray = getArrayForType(state, entityType);

            // Remove existing entry with same entityId (if exists)
            const filtered = currentArray.filter((i) => i.entityId !== item.entityId);

            // Add new item to front
            const updated = [item, ...filtered];

            // Limit to maxPerCategory
            const limited = updated.slice(0, state.maxPerCategory);

            setArrayForType(state, entityType, limited);
        },

        /**
         * Clear all recent activity (local only, resets on next login).
         */
        clearRecentActivity: (state) => {
            state.sessions = [];
            state.resources = [];
            state.users = [];
            state.registrations = [];
        },

        /**
         * Clear specific category (local only, resets on next login).
         */
        clearCategoryActivity: (state, action: PayloadAction<EntityType>) => {
            setArrayForType(state, action.payload, []);
        },
    },
});

export const {
    loadRecentActivity,
    addRecentActivity,
    clearRecentActivity,
    clearCategoryActivity,
} = recentActivitySlice.actions;

// Selectors

/**
 * Get all recent activity items sorted by time (across all categories).
 */
export const selectAllRecentActivity = (state: RootState): RecentActivityItem[] => {
    const all = [
        ...state.recentActivity.sessions,
        ...state.recentActivity.resources,
        ...state.recentActivity.users,
        ...state.recentActivity.registrations,
    ];
    return all.sort(
        (a, b) => new Date(b.lastAccessedUtc).getTime() - new Date(a.lastAccessedUtc).getTime()
    );
};

/**
 * Get recent sessions.
 */
export const selectRecentActivitySessions = (state: RootState): RecentActivityItem[] =>
    state.recentActivity.sessions;

/**
 * Get recent resources.
 */
export const selectRecentActivityResources = (state: RootState): RecentActivityItem[] =>
    state.recentActivity.resources;

/**
 * Get recent users.
 */
export const selectRecentActivityUsers = (state: RootState): RecentActivityItem[] =>
    state.recentActivity.users;

/**
 * Get recent registrations.
 */
export const selectRecentActivityRegistrations = (state: RootState): RecentActivityItem[] =>
    state.recentActivity.registrations;

/**
 * Get recent activity items by entity type.
 */
export const selectRecentActivityByType = (
    state: RootState,
    entityType: EntityType
): RecentActivityItem[] => {
    switch (entityType) {
        case "session":
            return state.recentActivity.sessions;
        case "resource":
            return state.recentActivity.resources;
        case "user":
            return state.recentActivity.users;
        case "registration":
            return state.recentActivity.registrations;
        default:
            return [];
    }
};

export default recentActivitySlice.reducer;
